import { useState } from 'react'
import { DateTime } from 'luxon'
import { Download } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/typography'
import { CSVFileUploader } from '@/components/csv-upload/shared/CSVFileUploader'
import type { RotationMember } from './RotationGrid'
import {
  buildRotationCsvTemplate,
  parseRotationCsv,
  type RotationCsvResult,
} from './rotation-csv'

// Upload the rotation sheet (week_start,Mon,…,Sun) and apply it onto the
// calendar. Applying overwrites the days the file covers — named cells assign,
// blank cells clear — and unresolved names land as fixable error cells in the
// grid. Nothing persists until the page's Save.
export default function RotationCsvDialog(props: {
  members: RotationMember[]
  todayIso: string
  onApply: (result: RotationCsvResult) => void
  onClose: () => void
}) {
  const { members, todayIso, onApply, onClose } = props
  const [file, setFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<RotationCsvResult | null>(null)

  const handleFile = (next: File | null) => {
    setFile(next)
    setParsed(null)
    if (!next) return
    const reader = new FileReader()
    reader.onload = () => {
      setParsed(
        parseRotationCsv(String(reader.result ?? ''), members, todayIso),
      )
    }
    reader.readAsText(next)
  }

  const assignedCount = parsed
    ? Object.values(parsed.edits).filter(Boolean).length
    : 0
  const emptyCount = parsed
    ? Object.values(parsed.edits).filter(id => id === null).length
    : 0
  const unknownCount = parsed ? Object.keys(parsed.unknownByDate).length : 0
  const pastCount = parsed?.pastDaysSkipped ?? 0
  const hasStructuralErrors = !!parsed && parsed.errors.length > 0

  // The date range the upload covers (past-skipped days excluded).
  const coveredDates = parsed
    ? [
        ...Object.keys(parsed.edits),
        ...Object.keys(parsed.unknownByDate),
      ].sort()
    : []
  const formatDate = (iso: string) => DateTime.fromISO(iso).toFormat('MMM d')
  const rangeLabel =
    coveredDates.length > 0
      ? `${formatDate(coveredDates[0])} – ${formatDate(coveredDates[coveredDates.length - 1])}`
      : null

  const plural = (count: number) => (count === 1 ? '' : 's')
  const canApply =
    !!parsed && !hasStructuralErrors && (assignedCount > 0 || unknownCount > 0)

  const downloadTemplate = () => {
    const blob = new Blob([buildRotationCsvTemplate(todayIso)], {
      type: 'text/csv',
    })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'on-call-rotation-template.csv'
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="gap-5">
        <DialogHeader>
          <DialogTitle>Upload rotation CSV</DialogTitle>
          <DialogDescription>
            Applying overwrites the days the file covers; you can still adjust
            cells before saving.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <CSVFileUploader uploadedFile={file} onFileChange={handleFile} />

          {hasStructuralErrors && (
            <div className="space-y-1 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2">
              {parsed!.errors.map(error => (
                <Text
                  key={error}
                  variant="muted-xs"
                  className="text-destructive"
                >
                  {error}
                </Text>
              ))}
            </div>
          )}

          {parsed && !hasStructuralErrors && (
            <div className="space-y-1 rounded-md border px-3 py-2 text-sm">
              {rangeLabel && <p className="font-medium">{rangeLabel}</p>}
              <p className="text-muted-foreground">
                {assignedCount} day{plural(assignedCount)} assigned
              </p>
              <p className="text-muted-foreground">
                {emptyCount} day{plural(emptyCount)} empty
              </p>
              {unknownCount > 0 && (
                <p className="text-destructive">
                  {unknownCount} name{plural(unknownCount)} not recognized — you
                  can reconcile directly on the calendar after upload
                </p>
              )}
              {pastCount > 0 && (
                <p className="text-muted-foreground">
                  {pastCount} day{plural(pastCount)} of the upload{' '}
                  {pastCount === 1 ? 'is' : 'are'} in the past — not updated
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="sm:mr-auto"
            onClick={downloadTemplate}
          >
            <Download className="mr-2 h-4 w-4" />
            Download template
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canApply}
            onClick={() => {
              if (parsed) onApply(parsed)
            }}
          >
            Apply to calendar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
